import { useEffect, useState } from "react";
import type { JSX } from "react";
import type { RecipeIngredient, RecipeStep, StepTimerDef } from "../../shared/recipes";
import { ingredientsForStep } from "./format-ingredient";
import { StepCard } from "./StepCard";
import type { ActiveTimer } from "./useRecipeTimers";

type StepListProps = {
  steps: readonly RecipeStep[];
  ingredients?: readonly RecipeIngredient[];
  defaultAllExpanded?: boolean;
  getStepDone?: (stepId: string) => boolean;
  onToggleStepDone?: (stepId: string) => void;
  scaleFactor?: number;
  getActiveTimer?: (stepId: string, timerDefId: string) => ActiveTimer | undefined;
  getTimerRemainingSeconds?: (timer: ActiveTimer) => number;
  onStartTimer?: (stepId: string, def: StepTimerDef) => void;
  onPauseTimer?: (timerId: string) => void;
  onResumeTimer?: (timerId: string) => void;
  onCancelTimer?: (timerId: string) => void;
};

function stepCanExpand(step: RecipeStep, ingredients: readonly RecipeIngredient[]): boolean {
  const hasDetails = step.details !== undefined && step.details.length > 0;
  return hasDetails || ingredientsForStep(step, ingredients).length > 0;
}

export function StepList({
  steps,
  ingredients = [],
  defaultAllExpanded = true,
  getStepDone,
  onToggleStepDone,
  scaleFactor = 1,
  getActiveTimer,
  getTimerRemainingSeconds,
  onStartTimer,
  onPauseTimer,
  onResumeTimer,
  onCancelTimer
}: StepListProps): JSX.Element {
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(() =>
    defaultAllExpanded ? new Set(steps.map((step) => step.id)) : new Set()
  );

  useEffect(() => {
    const stepIds = new Set(steps.map((step) => step.id));
    setExpandedIds((current) => {
      const next = new Set([...current].filter((id) => stepIds.has(id)));
      if (defaultAllExpanded) {
        for (const id of stepIds) {
          next.add(id);
        }
      }
      return next;
    });
  }, [steps, defaultAllExpanded]);

  const expandable = steps.some((step) => stepCanExpand(step, ingredients));
  const allExpanded = steps.length > 0 && steps.every((step) => expandedIds.has(step.id));

  return (
    <>
      <div className="step-list-header">
        <h3>Steps</h3>
        {expandable ? (
          <button
            type="button"
            className="text-button step-list-toggle-all"
            onClick={() => {
              setExpandedIds(allExpanded ? new Set() : new Set(steps.map((step) => step.id)));
            }}
          >
            {allExpanded ? "Collapse all" : "Expand all"}
          </button>
        ) : null}
      </div>
      <ol className="step-list cook-step-list">
        {steps.map((step, index) => (
          <li key={step.id}>
            <StepCard
              step={step}
              index={index}
              ingredients={ingredients}
              expanded={expandedIds.has(step.id)}
              canExpand={stepCanExpand(step, ingredients)}
              onToggleExpanded={() => {
                setExpandedIds((current) => {
                  const next = new Set(current);
                  if (next.has(step.id)) {
                    next.delete(step.id);
                  } else {
                    next.add(step.id);
                  }
                  return next;
                });
              }}
              done={getStepDone?.(step.id)}
              onToggleDone={
                onToggleStepDone === undefined
                  ? undefined
                  : () => {
                      onToggleStepDone(step.id);
                    }
              }
              scaleFactor={scaleFactor}
              getActiveTimer={
                getActiveTimer === undefined ? undefined : (timerDefId) => getActiveTimer(step.id, timerDefId)
              }
              getTimerRemainingSeconds={getTimerRemainingSeconds}
              onStartTimer={
                onStartTimer === undefined ? undefined : (def) => {
                  onStartTimer(step.id, def);
                }
              }
              onPauseTimer={onPauseTimer}
              onResumeTimer={onResumeTimer}
              onCancelTimer={onCancelTimer}
            />
          </li>
        ))}
      </ol>
    </>
  );
}
